<template>
  <section class="section">
    <div class="columns">
      <div class="column is-one-quarter">
      </div>
      <div class="column">
        <progress class="progress is-success" :value="i" :max="n"></progress>
        Question {{ i + 1 }} of {{ n }}

        <div class="card">
          <header class="card-header">
            <p class="card-header-title">{{ question.text }}</p>
          </header>
          <div class="card-image" v-if="imageURL">
            <figure class="image is-3by2">
              <img :src="imageURL"></img>
            </figure>
          </div>
          <div class="card-content">
            <div class="content">
              <div class="field" v-for="(option, j) in question.options">
                <div class="control">
                  <b-checkbox v-model="responses[i].choices" :native-value="j + 1">
                    {{ option.text}}
                  </b-checkbox>
                </div>
              </div>

              <div class="field">
                <div class="control">
                  <input v-model="responses[i].comments" class="input" type="text" placeholder="Comments" name="comments"></input>
                </div>
              </div>

              <div class="field">
                <a class="button" @click="goPrevious" v-if="canGoPrevious">
                  Back
                </a>

                <a class="button is-success" @click="goNext" :disabled="disableNext" v-if="canGoNext">
                  Next
                </a>

                <a class="button is-success" @click="submit" :disabled="disableNext" v-if="canSubmit">
                  Finish
                </a>

                <a class="button is-success" @click="loadMore" :disabled="disableNext" v-if="canLoadMore">
                  Load 10 more questions
                </a>

                <span class="tag is-large is-white is-hidden-tablet"><span class="has-text-danger">See map below</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-one-quarter">
        <figure class="image is-3by2">
          <img :src="heatmapURL"></img>
        </figure>
        <small>
          This map shows where we think you're most likely from in dark red based on your answer(s) to this question. Dark blue indicates regions with answers that differ the most from yours. If you selected multiple options, we've picked the one you selected first. (We don't have maps for all options yet, sorry!)
        </small>
      </div>
    </div>
  </section>
</template>

<script>
import surveyDefinition from '../survey';
import heatmaps from '../heatmaps';
import _ from '../mylodash';
import api from '../api';

const blankHeatmap = '/static/blank.png';
const unknownHeatmap = '/static/unknown.png';

export default {
  data() {
    return {
      questions: [],
      responses: [],
      i: 0,
      n: 0,
      loadingComponent: null,
    }
  },

  computed: {
    bestChoice () { return this.response.choices[0]; },

    heatmapDefined () { return !!heatmaps[`${this.question.id}-${this.bestChoice}`]; },

    heatmapURL () { return this.bestChoice ? (this.heatmapDefined ? `/static/heatmaps/heatmap-${this.question.id}-${this.bestChoice}.png` : unknownHeatmap) : blankHeatmap; },

    imageURL () { return this.question.image ? `/static/${this.question.image.replace('.png', '.jpg')}` : null; },

    question () { return this.questions[this.i]; },

    response () { return this.responses[this.i]; },

    hasPrevious () { return this.i > 0; },

    hasNext () { return !this.isLast; },

    hasSomeResponse () { return this.response.choices.length > 0; },

    isLast () { return this.i == this.n - 1; },

    disableNext() { return !this.hasSomeResponse; },

    canGoPrevious() { return this.hasPrevious; },

    canGoNext() { return this.hasNext; },

    canSubmit () { return this.isLast; },

    canLoadMore () { return this.isLast && this.n < surveyDefinition.n; },
  },

  created() {
    this.loading = true;
    this.generate();
    this.loading = false;
  },
  
  methods: {
    generate: function () {
      let data = surveyDefinition;

      this.questions = surveyDefinition.shuffle ? _.shuffle(surveyDefinition.questions) : surveyDefinition.questions;
      this.i = 0;
      this.n = surveyDefinition.subset;

      this.responses.splice(0, this.responses.length);

      for (let i = 0; i < surveyDefinition.n; i++) {
        let values = {
          id: this.questions[i].id,
          choices: [],
          comments: null
        };
        this.responses.push(values);
      }
    },

    loadMore: function () {
      if (this.canLoadMore && !this.disableNext) {
        this.n += surveyDefinition.additional_subsets; // eslint-disable-line camelcase

        if (this.n > surveyDefinition.n)
          this.n = surveyDefinition.n;

        this.goNext();
      }
    },

    goNext () {
      if (this.canGoNext && !this.disableNext) {
        if (this.i + 1 >= this.n)
          this.i = this.n - 1;
        else
          this.i++;

        this.$scrollTo('body');
      }
    },

    goPrevious () {
      if (this.canGoPrevious) {
        if (this.i < 0)
          this.i = 0;
        else
          this.i--;

        this.$scrollTo('body');
      }
    },

    submit () {
      if (this.canSubmit && !this.disableNext) {
        // Submit, then if success auto-logout
        let data = [];

        // Strip empty parts of the response
        for (let i = 0; i < this.responses.length; i++) {
          if (this.responses[i].choices.length > 0 || this.responses[i].comments) {
            let values = {
              id: this.responses[i].id,
            }

            if (this.responses[i].choices.length > 0)
              values.choices = this.responses[i].choices

            if (this.responses[i].comments)
              values.comments = this.responses[i].comments.replace(/\s+/g, ' ').trim()

            data.push(values);
          }
        }

        this.loadingComponent = this.$loading.open();

        api.postResponse({ data: data, completed: true })
          .then(request => this.submitSuccessful(request))
          .catch((error) => this.submitFailed(error));
      }
    },

    submitSuccessful (req) {
      this.loadingComponent.close();

      this.$store.dispatch('logout');
      delete localStorage.token;

      this.$router.push({ name: 'Thanks' });
    },

    submitFailed (error) {
      this.loadingComponent.close();
      api.handleError(error);
    },
  }
}
</script>

<style>
.card-header {
  background-color: #efefef;
}

.card-image {
  margin-top: 1em;
  margin-bottom: 1em;
  margin-left: auto;
  margin-right: auto;
  max-width: 336px;
}
</style>



// WEBPACK FOOTER //
// src/components/Questions.vue